import React from "react";
import { Link } from "react-router-dom";
import "./CarLoan.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const carLoanUses = [
  {
    icon: "bi bi-car-front",
    title: "New Car Purchase",
    text:
      "Finance an eligible new vehicle while managing the purchase cost through structured monthly repayments.",
  },
  {
    icon: "bi bi-car-front-fill",
    title: "Used Car Purchase",
    text:
      "Explore financing options for an eligible pre-owned vehicle, subject to vehicle age and lender policy.",
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Car Loan Balance Transfer",
    text:
      "Transfer an eligible existing car loan to another lender for potentially improved rates or repayment terms.",
  },
  {
    icon: "bi bi-lightning-charge",
    title: "Electric Vehicle Finance",
    text:
      "Explore suitable loan options for eligible electric cars and other approved vehicle categories.",
  },
  {
    icon: "bi bi-briefcase",
    title: "Commercial Vehicle Use",
    text:
      "Certain lenders may provide vehicle-finance options for eligible business or commercial requirements.",
  },
  {
    icon: "bi bi-plus-circle",
    title: "Additional Funding",
    text:
      "Eligible borrowers may receive additional finance or refinancing options, subject to lender assessment.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Car-loan applicant profile",
    title: "Applicant Profile",
    text:
      "Salaried, self-employed and eligible business applicants may apply.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Car-loan applicant age",
    title: "Applicant Age",
    text:
      "Age criteria depend on the selected lender, applicant profile and loan tenure.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Employment and business stability",
    title: "Income Stability",
    text:
      "Stable employment, professional income or established business activity may be required.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Car-loan repayment capacity",
    title: "Repayment Capacity",
    text:
      "Eligibility depends on income, existing EMIs, credit score and banking history.",
  },
];

const documentGroups = [
  {
    title: "Identity Documents",
    image: "/images/Bank Pages/photo-identy.png",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Voter ID",
      "Driving Licence",
      "Recent passport-size photographs",
    ],
  },
  {
    title: "Address Documents",
    image: "/images/Bank Pages/proof-of-residence.png",
    items: [
      "Aadhaar Card",
      "Passport",
      "Driving Licence",
      "Registered rent agreement",
      "Recent electricity or utility bill",
      "Bank statement showing current address",
    ],
  },
  {
    title: "Income Documents",
    image: "/images/Bank Pages/income-proof.png",
    items: [
      "Recent salary slips",
      "Salary account bank statements",
      "Form 16, where applicable",
      "Income tax returns",
      "Audited financial statements for self-employed applicants",
      "Business registration proof, where applicable",
    ],
  },
  {
    title: "Vehicle and Supporting Documents",
    image: "/images/Bank Pages/other-document.png",
    items: [
      "Vehicle quotation or proforma invoice",
      "Dealer invoice",
      "Booking receipt",
      "Vehicle registration details for used cars",
      "Existing car-loan statement for balance transfer",
      "Additional lender-specific documents",
    ],
  },
];

const applicationSteps = [
  {
    title: "Share Your Requirement",
    text:
      "Tell us the vehicle type, purchase value, preferred loan amount and applicant profile.",
  },
  {
    title: "Initial Eligibility Review",
    text:
      "Our team reviews income, existing obligations, credit profile and basic eligibility.",
  },
  {
    title: "Compare Loan Options",
    text:
      "Compare indicative rates, tenure, down payment, fees and lender requirements.",
  },
  {
    title: "Submit Documents",
    text:
      "Submit KYC, income, banking and vehicle-related documents with our assistance.",
  },
  {
    title: "Lender Verification",
    text:
      "The lender reviews the application, credit history, repayment capacity and vehicle details.",
  },
  {
    title: "Approval and Disbursement",
    text:
      "After approval and completion of formalities, the sanctioned amount is disbursed as per lender process.",
  },
];

const carLoanRates = [
  {
    bank: "Bank of Maharashtra",
    link:
      "https://bankofmaharashtra.in/personal-banking/loans/car-loan",
    rate: "7.45%*",
    amount: "Up to ₹50 lakh",
    tenure: "Up to 8 years",
    fee: "As per bank policy",
  },
  {
    bank: "Central Bank of India",
    link: "https://www.centralbankofindia.co.in/en/node/418",
    rate: "7.45%*",
    amount: "As per eligibility",
    tenure: "Up to 84 months",
    fee: "As per bank policy",
  },
  {
    bank: "Indian Overseas Bank",
    link: "https://www.iob.in/Vehicle-loans",
    rate: "7.45%*",
    amount: "As per eligibility",
    tenure: "Up to 84 months",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of India",
    link: "https://bankofindia.co.in/vehicle-loan1",
    rate: "8.75%*",
    amount: "Up to ₹70 lakh",
    tenure: "Up to 7 years",
    fee: "As per bank policy",
  },
];

const transferBenefits = [
  "Potentially lower interest rate",
  "Possible reduction in monthly EMI",
  "More suitable repayment tenure",
  "Transparent transfer-cost comparison",
  "Assistance with existing lender documents",
  "Application support from transfer to closure",
];

const faqItems = [
  {
    question: "Who can apply for a car loan?",
    answer:
      "Salaried, self-employed and eligible business applicants may apply. Final eligibility depends on income, age, credit profile, obligations and lender policy.",
  },
  {
    question: "Can I finance a used car?",
    answer:
      "Many lenders offer used-car financing. Eligibility depends on the vehicle’s age, condition, valuation, ownership documents and lender policy.",
  },
  {
    question: "How much down payment is required?",
    answer:
      "The required contribution depends on the lender, vehicle category, on-road price, applicant profile and eligible loan-to-value ratio.",
  },
  {
    question: "What interest rate will I receive?",
    answer:
      "The final rate depends on the selected lender, applicant profile, credit score, vehicle type, loan amount and prevailing lender policy.",
  },
  {
    question: "Which documents are generally required?",
    answer:
      "Common documents include PAN, Aadhaar, address proof, income proof, bank statements and the vehicle quotation or invoice.",
  },
  {
    question: "How long does car-loan approval take?",
    answer:
      "The timeline depends on document completeness, credit assessment, applicant verification and lender processing.",
  },
  {
    question: "Can I transfer my existing car loan?",
    answer:
      "Eligible borrowers may transfer an existing car loan to another lender for potentially better repayment terms, subject to assessment and transfer charges.",
  },
  {
    question: "Are processing fees applicable?",
    answer:
      "Lenders may charge processing, documentation, valuation or other applicable fees. Exact charges should be confirmed before applying.",
  },
];

function CarLoan() {
  const [openFaq, setOpenFaq] = React.useState(0);

  return (
    <>
      <Seo
        title="Car Loan in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore car-loan options in Pune with MONAYRA (OPC) PRIVATE LIMITED. Get assistance with eligibility, lender comparison, documentation and vehicle-loan processing."
        keywords="car loan in Pune, vehicle loan, used car loan, new car loan, car loan balance transfer"
        canonical="https://www.mortbuzz.in/car-loan"
      />

      <main className="monayra-car-loan-page">
        <section className="car-loan-hero">
          <div className="car-loan-hero-overlay"></div>

          <div className="car-loan-hero-container">
            <span className="car-loan-hero-eyebrow animate__animated animate__fadeInDown">
              Vehicle Finance Solutions
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Drive home your dream car with
              <span> suitable financing</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Compare eligible new-car, used-car and balance-transfer options
              with professional assistance for documentation and lender
              processing.
            </p>

            <div className="car-loan-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link to="/apply-now" className="car-loan-primary-btn">
                Apply for Car Loan
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a
                href="#car-loan-rates"
                className="car-loan-secondary-btn"
              >
                <i className="bi bi-bar-chart"></i>
                View Indicative Rates
              </a>
            </div>

            <div className="car-loan-hero-features">
              <div>
                <i className="bi bi-car-front"></i>
                New and used car options
              </div>

              <div>
                <i className="bi bi-bank"></i>
                Multiple lending partners
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation assistance
              </div>
            </div>
          </div>
        </section>

        <section className="car-loan-intro-section">
          <div className="car-loan-page-container car-loan-intro-grid">
            <div className="car-loan-intro-image">
              <img
                src="/images/Bank Pages/night-drive-supercar-scaled.jpg"
                alt="Car financing assistance"
              />

              <div className="car-loan-image-overlay"></div>

              <div className="car-loan-rate-card">
                <span>Indicative starting rate</span>
                <strong>7.45%*</strong>
                <small>Subject to lender assessment</small>
              </div>
            </div>

            <div className="car-loan-intro-content">
              <span className="car-loan-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Car-loan assistance designed around
                <span> your purchase</span>
              </h2>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED helps eligible applicants
                compare vehicle-finance options from selected banks and
                financial institutions.
              </p>

              <p>
                Our team assists with basic eligibility review, document
                collection, lender coordination, application tracking and
                disbursement support.
              </p>

              <div className="car-loan-benefits">
                {[
                  "Indicative rates starting from 7.45%*",
                  "New and used car financing",
                  "Balance-transfer assistance",
                  "Flexible repayment options",
                  "Multiple lender comparison",
                  "Support until disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="car-loan-inline-btn"
              >
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="car-loan-uses-section">
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Vehicle Finance Options
              </span>

              <h2>
                Common uses of a
                <span> car loan</span>
              </h2>

              <p>
                Vehicle finance can support new-car purchases, used-car
                purchases, refinancing and other eligible requirements.
              </p>
            </div>

            <div className="car-loan-uses-grid">
              {carLoanUses.map((item) => (
                <article
                  className="car-loan-use-card"
                  key={item.title}
                >
                  <span className="car-loan-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="car-loan-eligibility-section">
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Car-loan
                <span> eligibility factors</span>
              </h2>

              <p>
                The lender evaluates income, employment or business profile,
                credit history and repayment capacity.
              </p>
            </div>

            <div className="car-loan-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="car-loan-eligibility-card"
                  key={item.title}
                >
                  <div className="car-loan-eligibility-image">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="car-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final eligibility depends on the selected lender’s policies
                and complete applicant assessment.
              </span>
            </div>
          </div>
        </section>

        <section className="car-loan-cta-section">
          <div className="car-loan-page-container">
            <div className="car-loan-cta-card">
              <div>
                <span>Begin Your Application</span>

                <h2>Ready to finance your next car?</h2>

                <p>
                  Share your vehicle and income details to explore suitable
                  financing options.
                </p>
              </div>

              <Link to="/apply-now" className="car-loan-primary-btn">
                Apply Now
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="car-loan-documents-section">
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Document Checklist
              </span>

              <h2>
                Documents generally required for a
                <span> car loan</span>
              </h2>

              <p>
                The final checklist varies according to applicant profile,
                vehicle type and lender requirements.
              </p>
            </div>

            <div className="car-loan-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="car-loan-document-card"
                  key={group.title}
                >
                  <div className="car-loan-document-heading">
                    <span>
                      <img
                        src={group.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="car-loan-process-section">
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A simple car-loan
                <span> application process</span>
              </h2>
            </div>

            <div className="car-loan-process-grid">
              {applicationSteps.map((step, index) => (
                <article
                  className="car-loan-process-card"
                  key={step.title}
                >
                  <span className="car-loan-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          id="car-loan-rates"
          className="car-loan-rates-section"
        >
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Indicative Bank Comparison
              </span>

              <h2>
                Car-loan
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates, loan limits, tenure and charges are indicative and
                should be confirmed with the selected lender.
              </p>
            </div>

            <div className="car-loan-table-wrapper">
              <table className="car-loan-rate-table">
                <thead>
                  <tr>
                    <th>Bank</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Maximum Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {carLoanRates.map((bank) => (
                    <tr key={bank.bank}>
                      <td>
                        <a
                          href={bank.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bank.bank}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{bank.rate}</strong>
                      </td>

                      <td>{bank.amount}</td>
                      <td>{bank.tenure}</td>
                      <td>{bank.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="car-loan-table-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="car-loan-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final rates and terms depend on applicant eligibility,
                vehicle details and lender policy.
              </span>
            </div>
          </div>
        </section>

        <section className="car-loan-fee-section">
          <div className="car-loan-page-container car-loan-fee-grid">
            <div className="car-loan-fee-image">
              <img
                src="/images/FrontPage/loan-dealing.jpg"
                alt="Car-loan processing discussion"
                loading="lazy"
              />
            </div>

            <div className="car-loan-fee-content">
              <span className="car-loan-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing fee</span>
              </h2>

              <p>
                Lenders may charge a processing fee to review documents,
                conduct credit checks and process the vehicle-loan
                application.
              </p>

              <p>
                The exact charge depends on the lender, vehicle type, loan
                amount, applicant profile and available offer. Applicable
                taxes may also be charged.
              </p>

              <div className="car-loan-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>Compare the complete borrowing cost</strong>

                  <span>
                    Review the interest rate, processing fee, insurance,
                    documentation and other applicable charges.
                  </span>
                </div>
              </div>

              <Link
                to="/apply-now"
                className="car-loan-inline-btn"
              >
                Get Suitable Offers
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="car-loan-emi-section">
          <div className="car-loan-page-container car-loan-emi-grid">
            <div className="car-loan-emi-content">
              <span className="car-loan-section-eyebrow">
                Plan Your Repayment
              </span>

              <h2>
                Estimate your monthly
                <span> car-loan EMI</span>
              </h2>

              <p>
                Use an EMI calculator to estimate your monthly payment based
                on the required loan amount, interest rate and repayment
                tenure.
              </p>

              <div className="car-loan-emi-points">
                <div>
                  <i className="bi bi-check-circle"></i>
                  Estimate monthly repayment
                </div>

                <div>
                  <i className="bi bi-check-circle"></i>
                  Compare different tenures
                </div>

                <div>
                  <i className="bi bi-check-circle"></i>
                  Plan your vehicle budget
                </div>
              </div>

              <Link
                to="/emi-calculator"
                className="car-loan-inline-btn"
              >
                Open EMI Calculator
                <i className="bi bi-calculator"></i>
              </Link>
            </div>

            <div className="car-loan-emi-image">
              <img
                src="/images/FrontPage/calculate.jpg"
                alt="Car-loan EMI calculator"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className="car-loan-transfer-section">
          <div className="car-loan-page-container car-loan-transfer-grid">
            <div className="car-loan-transfer-image">
              <img
                src="/images/Bank Pages/carloan.png"
                alt="Car-loan balance-transfer assistance"
                loading="lazy"
              />
            </div>

            <div className="car-loan-transfer-content">
              <span className="car-loan-section-eyebrow">
                Existing Loan Transfer
              </span>

              <h2>
                Explore a car-loan
                <span> balance transfer</span>
              </h2>

              <p>
                A car-loan balance transfer allows an eligible borrower to
                move the outstanding loan to another lender offering
                potentially better repayment terms.
              </p>

              <div className="car-loan-benefits">
                {transferBenefits.map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link
                to="/apply-now"
                className="car-loan-inline-btn"
              >
                Check Transfer Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="car-loan-faq-section">
          <div className="car-loan-page-container">
            <div className="car-loan-section-header">
              <span className="car-loan-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Car-loan
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="car-loan-faq-layout">
              <div className="car-loan-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`car-loan-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="car-loan-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="car-loan-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="car-loan-help-card">
                <span className="car-loan-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="car-loan-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>Speak with our car-loan team</h3>

                <p>
                  Share your vehicle price, required loan amount, income
                  profile and city to understand suitable next steps.
                </p>

                <Link
                  to="/contact"
                  className="car-loan-primary-btn"
                >
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/apply-now"
                  className="car-loan-help-secondary"
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        <section className="car-loan-disclaimer">
          <div className="car-loan-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates, amounts and product details are indicative.
              Final approval, rate, tenure, loan amount, down payment, fees and
              other terms are determined by the selected lender after complete
              assessment. MONAYRA (OPC) PRIVATE LIMITED provides loan
              assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default CarLoan;